import { Dispatch, SetStateAction, useCallback } from 'react';
import { AlertType, ButtonColor, ButtonGroup, ButtonVariant, Icon, IconButton } from '../../components';
import { useHandleApiResponse, useToast } from '../../hooks';
import { useDeleteKnowledgeHubMutation } from '../../services';
import { ComponentSize } from '../../types';

interface KnowledgeHubItemActionsProps {
  currPage: number;
  currPageSize: number;
  recordId: string;
  totalPages: number;
  enterEditMode: () => void;
  refetchKnowledgeHub: () => void;
  setCurrPage: Dispatch<SetStateAction<number>>;
}

const KnowledgeHubItemActions = ({
  currPage,
  currPageSize,
  recordId,
  totalPages,
  enterEditMode,
  refetchKnowledgeHub,
  setCurrPage,
}: KnowledgeHubItemActionsProps) => {
  const [deleteKnowledgeHub, { isLoading: isDeleteLoading }] = useDeleteKnowledgeHubMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const handleDelete = useCallback(async () => {
    try {
      const response = await deleteKnowledgeHub(recordId);
      handleApiResponse({
        response,
        errorMsg: 'Failed to delete knowledge hub record. Please try again.',
        onSuccess: () => {
          if (currPageSize === 1 && currPage > 0 && currPage === totalPages - 1) {
            // If we are deleting the last record in the last page, fetch the previous page.
            setCurrPage((prev) => prev - 1);
          } else {
            // Otherwise, refetch the current page.
            refetchKnowledgeHub();
          }
        },
      });
    } catch (error) {
      console.error(error);
      showToast({ message: 'Something went wrong. Please try again.', type: AlertType.ERROR });
    }
  }, [
    currPage,
    currPageSize,
    recordId,
    totalPages,
    deleteKnowledgeHub,
    handleApiResponse,
    refetchKnowledgeHub,
    setCurrPage,
    showToast,
  ]);

  return (
    <ButtonGroup className="bg-base-100 rounded-full p-1" size={ComponentSize.X_SMALL}>
      <IconButton disabled={isDeleteLoading} icon={Icon.EDIT} variant={ButtonVariant.GHOST} onClick={enterEditMode} />
      <IconButton
        color={ButtonColor.DESTRUCTIVE}
        icon={Icon.TRASH}
        loading={isDeleteLoading}
        variant={ButtonVariant.GHOST}
        onClick={handleDelete}
      />
    </ButtonGroup>
  );
};

export default KnowledgeHubItemActions;
